import React, { useState, useEffect, useRef, useCallback } from "react";

const API_URL = "https://assocham.org/assocham_backend/api/get_privacy_policy.php";

function slugify(text, index) {
  const base = (text || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
  return `${base || "section"}-${index}`;
}

// Walks the fetched HTML, gives every h2/h3 a stable id, and returns
// a flat table-of-contents list alongside the (now id-tagged) HTML.
function withHeadingIds(html) {
  if (typeof window === "undefined" || !html) {
    return { html: html || "", toc: [] };
  }
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const headings = Array.from(doc.querySelectorAll("h2, h3"));

  const toc = headings.map((heading, index) => {
    const id = slugify(heading.textContent, index);
    heading.id = id;
    return {
      id,
      text: heading.textContent.trim(),
      level: heading.tagName === "H2" ? 2 : 3,
    };
  });

  return { html: doc.body.innerHTML, toc };
}

// The `title` column comes back as raw HTML (e.g. wrapped in a <p> tag
// with inline styles from a WYSIWYG editor). We only want the plain text
// for the banner heading — the banner already has its own styling.
function stripHtml(html) {
  if (typeof window === "undefined" || !html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return (doc.body.textContent || "").trim();
}

function formatDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export default function PrivacyPolicy() {
  const [status, setStatus] = useState("loading"); // loading | success | error
  const [policy, setPolicy] = useState(null);
  const [toc, setToc] = useState([]);
  const [activeId, setActiveId] = useState("");
  const contentRef = useRef(null);

  // Load Lora (headings) + Inter (body) once, without touching global CSS.
  useEffect(() => {
    const id = "privacy-policy-fonts";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=Lora:wght@500;600;700&display=swap";
    document.head.appendChild(link);
  }, []);

  const fetchPolicy = useCallback(() => {
    setStatus("loading");
    fetch(API_URL)
      .then((res) => {
        if (!res.ok) throw new Error("Request failed");
        return res.json();
      })
      .then((res) => {
        if (!res.success || !res.data) {
          throw new Error(res.message || "No data returned");
        }
        const { html, toc } = withHeadingIds(res.data.content);
        setPolicy({
          title: stripHtml(res.data.title) || "Privacy Policy",
          html,
          updatedAt: formatDate(res.data.updated_at),
        });
        setToc(toc);
        setStatus("success");
      })
      .catch(() => setStatus("error"));
  }, []);

  useEffect(() => {
    fetchPolicy();
  }, [fetchPolicy]);

  // Highlight whichever section is currently in view.
  useEffect(() => {
    if (status !== "success" || toc.length === 0) return;
    const headingEls = toc.map((item) => document.getElementById(item.id)).filter(Boolean);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: "-15% 0px -70% 0px", threshold: 0 }
    );

    headingEls.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [status, toc]);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const inlineStyles = `
    .policy-content { font-family: 'Inter', sans-serif; color: #24211D; font-size: 1.0625rem; line-height: 1.75; }
    .policy-content h2 { font-family: 'Lora', serif; font-size: 1.5rem; font-weight: 600; color: #da2128; margin: 2.75rem 0 0.75rem; padding-bottom: 0.6rem; border-bottom: 1px solid #E3DDD1; }
    .policy-content h2:first-child { margin-top: 0; }
    .policy-content h3 { font-family: 'Lora', serif; font-size: 1.15rem; font-weight: 600; color: #da2128; margin: 1.75rem 0 0.5rem; }
    .policy-content p { margin: 0 0 1.1rem; max-width: 68ch; }
    .policy-content b, .policy-content strong { color: #da2128; font-weight: 600; }
    .policy-content ul { margin: 0.75rem 0 1.25rem; padding-left: 0; list-style: none; }
    .policy-content li { position: relative; padding-left: 1.4rem; margin-bottom: 0.6rem; max-width: 66ch; }
    .policy-content li::before { content: ""; position: absolute; left: 0; top: 0.65rem; width: 6px; height: 6px; background: #B8894B; border-radius: 50%; }
    .policy-content a { color: #B8894B; text-decoration: underline; text-underline-offset: 2px; }
  `;

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-[#FAF8F4]">
        <div className="h-44 bg-[#da2128] sm:h-52" />
        <div className="mx-auto max-w-6xl px-6 py-12 sm:py-16">
          <div className="space-y-4">
            <div className="h-4 w-1/3 animate-pulse rounded bg-[#E3DDD1]" />
            <div className="h-4 w-full animate-pulse rounded bg-[#E3DDD1]" />
            <div className="h-4 w-5/6 animate-pulse rounded bg-[#E3DDD1]" />
            <div className="h-4 w-full animate-pulse rounded bg-[#E3DDD1]" />
            <div className="h-4 w-2/3 animate-pulse rounded bg-[#E3DDD1]" />
          </div>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-[#FAF8F4] px-6">
        <div className="max-w-md rounded-xl border border-[#E3DDD1] bg-white p-8 text-center shadow-sm">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#FBEFE3]">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#B8894B" strokeWidth="2">
              <path d="M12 9v4M12 17h.01M10.29 3.86l-8.18 14.18A2 2 0 0 0 3.82 21h16.36a2 2 0 0 0 1.71-3.01L13.71 3.86a2 2 0 0 0-3.42 0z" />
            </svg>
          </div>
          <h2 style={{ fontFamily: "'Lora', serif" }} className="text-lg font-semibold text-[#da2128]">
            Couldn't load the privacy policy
          </h2>
          <p className="mt-2 text-sm text-[#5B564B]">
            Something went wrong while reaching the server. Please check your connection and try again.
          </p>
          <button
            onClick={fetchPolicy}
            className="mt-5 rounded-full bg-[#da2128] px-5 py-2 text-sm font-medium text-white transition hover:bg-[#1C3A63]"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  const hasToc = toc.length > 0;

  return (
    <div className="min-h-screen bg-[#FAF8F4]">
      <style>{inlineStyles}</style>

      {/* Banner */}
      <header className="relative overflow-hidden bg-[#da2128]">
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              "linear-gradient(135deg, transparent 48%, #B8894B 48%, #B8894B 52%, transparent 52%)",
            backgroundSize: "34px 34px",
          }}
        />
        <div className="relative mx-auto max-w-6xl px-6 py-16 sm:py-20">
          <p className="text-sm text-[#D8C39B]">The Associated Chambers of Commerce and Industry of India</p>
          <h1
            style={{ fontFamily: "'Lora', serif" }}
            className="mt-3 text-4xl font-semibold text-white sm:text-5xl"
          >
            {policy.title}
          </h1>
          {/* {policy.updatedAt && (
            // <p className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#B8894B]/40 bg-white/5 px-4 py-1.5 text-sm text-[#EFE6D3]">
            //   Last updated {policy.updatedAt}
            // </p>
          )} */}
        </div>
        <div className="h-[3px] w-full bg-gradient-to-r from-[#B8894B] via-[#E8D4A8] to-[#B8894B]" />
      </header>

      {/* Body */}
      <main className="mx-auto max-w-6xl px-6 py-12 sm:py-16">
        <div className={hasToc ? "lg:grid lg:grid-cols-[240px_1fr] lg:gap-12" : ""}>
          {hasToc && (
            <>
              {/* Mobile jump-nav */}
              <details className="mb-8 rounded-lg border border-[#E3DDD1] bg-white p-4 lg:hidden">
                <summary className="cursor-pointer text-sm font-medium text-[#da2128]">
                  Jump to a section
                </summary>
                <nav className="mt-3 flex flex-col gap-2">
                  {toc.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      className={`text-left text-sm ${
                        item.level === 3 ? "pl-4 text-[#5B564B]" : "text-[#24211D]"
                      } hover:text-[#B8894B]`}
                    >
                      {item.text}
                    </button>
                  ))}
                </nav>
              </details>

              {/* Desktop sidebar */}
              <aside className="hidden lg:block">
                <nav className="sticky top-10 border-l border-[#E3DDD1] pl-5">
                  <p style={{ fontFamily: "'Lora', serif" }} className="mb-3 text-sm font-medium text-[#da2128]">
                    On this page
                  </p>
                  <ul className="space-y-2.5">
                    {toc.map((item) => (
                      <li key={item.id}>
                        <button
                          onClick={() => scrollToSection(item.id)}
                          className={`text-left text-sm leading-snug transition-colors ${
                            item.level === 3 ? "pl-3" : ""
                          } ${
                            activeId === item.id
                              ? "font-medium text-[#B8894B]"
                              : "text-[#5B564B] hover:text-[#da2128]"
                          }`}
                        >
                          {item.text}
                        </button>
                      </li>
                    ))}
                  </ul>
                </nav>
              </aside>
            </>
          )}

          <article
            ref={contentRef}
            className="policy-content max-w-none rounded-xl border border-[#E3DDD1] bg-white p-6 sm:p-10"
            dangerouslySetInnerHTML={{ __html: policy.html }}
          />
        </div>
      </main>
    </div>
  );
}